import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDeclaracion, saveDeclaracion, generatePdf } from '../services/declaracionRenta/f22Service';
import { getModule } from '../components/DeclaracionRenta/F22/registry';
import { showSuccess, showError, showLoading } from '../utils/alerts';
import Swal from 'sweetalert2';

/**
 * Hook principal para gestión del formulario F-22.
 * Carga dinámicamente el módulo correcto según año tributario y régimen.
 * Hace exactamente UNA llamada API al montar (si hay backend disponible).
 *
 * @param {number} anioTributario - Año tributario (ej: 2026)
 * @param {string} regimen - Régimen tributario ('14d3' o '14d8')
 */
const useF22Form = (anioTributario = 2026, regimen = '14d3') => {
  const { selectedCompany, token } = useAuth();

  // Cargar módulo dinámicamente según año y régimen
  const mod = useMemo(() => getModule(anioTributario, regimen), [anioTributario, regimen]);
  const { recalculateFn, createInitialStateFn } = useMemo(() => ({
    recalculateFn: mod?.recalculate || (() => ({})),
    createInitialStateFn: mod?.createInitialState || (() => ({})),
  }), [mod]);

  const [data, setData] = useState(() => createInitialStateFn());
  const [computed, setComputed] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const autoSaveTimer = useRef(null);
  const loadedRef = useRef(false);
  const recalcTimer = useRef(null);

  const idCompany = selectedCompany?.id_company || selectedCompany?.id;

  // Recalcula todas las fórmulas (inmediato, para carga inicial)
  const doRecalculateImmediate = useCallback((currentData) => {
    try {
      const result = recalculateFn(currentData);
      setComputed(result);
    } catch (err) {
      console.error('Error en recálculo de fórmulas:', err);
    }
  }, [recalculateFn]);

  // Recalcula con debounce (para edición de campos - evita recalcular en cada tecla)
  const doRecalculate = useCallback((currentData) => {
    if (recalcTimer.current) clearTimeout(recalcTimer.current);
    recalcTimer.current = setTimeout(() => {
      doRecalculateImmediate(currentData);
    }, 150);
  }, [doRecalculateImmediate]);

  // Actualiza un campo del formulario (soporta paths anidados)
  const updateField = useCallback((section, field, value) => {
    setData((prev) => {
      const updated = {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      };
      doRecalculate(updated);
      setDirty(true);
      return updated;
    });
  }, [doRecalculate]);

  // Actualiza un campo anidado profundo (ej: antecedentes.socios[0].nombre)
  const updateNestedField = useCallback((section, path, value) => {
    setData((prev) => {
      const updated = { ...prev, [section]: { ...prev[section] } };
      const keys = path.split('.');
      let current = updated[section];
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const match = key.match(/^(\w+)\[(\d+)\]$/);
        if (match) {
          const arrKey = match[1];
          const idx = parseInt(match[2]);
          current[arrKey] = [...current[arrKey]];
          current[arrKey][idx] = { ...current[arrKey][idx] };
          current = current[arrKey][idx];
        } else {
          current[key] = { ...current[key] };
          current = current[key];
        }
      }
      const lastKey = keys[keys.length - 1];
      current[lastKey] = value;

      doRecalculate(updated);
      setDirty(true);
      return updated;
    });
  }, [doRecalculate]);

  // Guarda la declaración en el servidor
  const save = useCallback(async (silent = false) => {
    if (!idCompany || !token) return;
    setSaving(true);
    try {
      if (!silent) showLoading('Guardando declaración...');
      await saveDeclaracion(idCompany, anioTributario, regimen, data);
      setIsNew(false);
      setDirty(false);
      setPdfUrl(null); // Al guardar nuevos datos, el PDF anterior queda obsoleto
      if (!silent) {
        Swal.close();
        showSuccess('Declaración guardada correctamente');
      }
    } catch (err) {
      console.error('Error al guardar declaración:', err);
      if (!silent) showError('Error', 'No se pudo guardar la declaración');
    } finally {
      setSaving(false);
    }
  }, [idCompany, anioTributario, regimen, data, token]);

  // Genera PDF server-side, sube a R2 y guarda URL en BD
  const generatePdfFn = useCallback(async (html) => {
    if (!idCompany || !token) return;
    setGeneratingPdf(true);
    try {
      showLoading('Generando PDF...');
      const result = await generatePdf(idCompany, anioTributario, regimen, html);
      setPdfUrl(result.data?.pdf_url);
      Swal.close();
      showSuccess('PDF generado correctamente');
      return result.data?.pdf_url;
    } catch (err) {
      console.error('Error al generar PDF:', err);
      Swal.close();
      showError('Error', 'No se pudo generar el PDF');
    } finally {
      setGeneratingPdf(false);
    }
  }, [idCompany, anioTributario, regimen, token]);

  // Carga inicial: exactamente UNA llamada al montar el componente
  // loadedRef previene ejecuciones duplicadas (incluyendo StrictMode)
  useEffect(() => {
    if (loadedRef.current) return;
    if (!idCompany || !token) return;

    loadedRef.current = true;

    const load = async () => {
      setLoading(true);
      try {
        const loaders = mod?.dataLoaders || [];
        const [response, ...loaderResults] = await Promise.all([
          getDeclaracion(idCompany, anioTributario, regimen).catch((err) => {
            if (err.response?.status !== 404) console.error('Error al cargar declaración:', err);
            return null;
          }),
          ...loaders.map((dl) =>
            dl.loader(idCompany, token).catch((err) => {
              console.error(`Error en dataLoader "${dl.key}":`, err);
              return null;
            })
          ),
        ]);

        const hasSaved = !!response?.data;
        if (response?.pdf_url) setPdfUrl(response.pdf_url);
        let formData = response?.data || createInitialStateFn();

        loaders.forEach((dl, i) => {
          if (loaderResults[i]) {
            formData = dl.apply(formData, loaderResults[i]);
          }
        });

        setIsNew(!hasSaved);
        setData(formData);
        doRecalculateImmediate(formData);
        setDirty(false);
      } catch (err) {
        console.error('Error al cargar declaración:', err);
      } finally {
        setLoaded(true);
        setLoading(false);
      }
    };

    load();
  }, [idCompany, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalcula al montar con datos iniciales
  useEffect(() => {
    doRecalculateImmediate(data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-guardado cada 60 segundos si hay cambios
  useEffect(() => {
    if (dirty && loaded) {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = setTimeout(() => {
        save(true);
      }, 60000);
    }
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [dirty, loaded, save]);

  // Limpiar timer de recálculo al desmontar
  useEffect(() => {
    return () => {
      if (recalcTimer.current) clearTimeout(recalcTimer.current);
    };
  }, []);

  return {
    data,
    computed,
    loading,
    saving,
    dirty,
    isNew,
    pdfUrl,
    generatingPdf,
    updateField,
    updateNestedField,
    save,
    generatePdfFn,
    idCompany,
  };
};

export default useF22Form;
